import { create } from "zustand";
import { DateTime } from "luxon";
import { persist } from "zustand/middleware";
import { getUrl } from "../getUrl";

export class InvalidCredentials extends Error {
  constructor(options: ErrorOptions = {}) {
    super("Invalid credentials for login.", options);
  }
}

export class UnknownLoginError extends Error {
  constructor(
    private responsePayload: object | null,
    options: ErrorOptions = {},
  ) {
    super("An unexpected error occurred during authentication.", options);
  }
}

export class AuthServerUnreachable extends Error {
  constructor(options: ErrorOptions = {}) {
    super("Could not reach the authentication server.", options);
  }
}

const REFRESH_RETRY_DELAYS_MS = [500, 1500];
const TRANSIENT_STATUSES = [408, 429];

function isTransient(status: number): boolean {
  return status >= 500 || TRANSIENT_STATUSES.includes(status);
}

type SsoProvider = { provider_name: string } | null;
export type TokenPayload = {
  exp: number;
  iat: number;
  email?: string;
  sub?: string;
  id?: string;
  type?: "user" | "aurea" | "reseller";
  sso_provider?: SsoProvider;
  [key: string]: unknown;
};

interface Tokens {
  access_token: string;
  refresh_token: string;
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  username: string | null;
  accessTokenExpiry: DateTime | null;
  accessTokenPayload: TokenPayload | null;
  refreshTokenExpiry: DateTime | null;
  agroManagerEnv: string | null;
  login: (credentials: {
    email: string;
    password: string;
  }) => Promise<Error | null>;
  getToken: () => Promise<string | null>;
  logout: () => void;
  setTokens: (token: Tokens, email: string) => void;
  setAgroManagerEnv: (env: string | null) => void;
}

export const useAuth = create(
  persist<AuthState>(
    (set, get) => {
      async function authenticate(
        payload: Record<string, string>,
      ): Promise<Error | null> {
        let tokenResponse: Response;
        try {
          tokenResponse = await fetch(getUrl("/v1/oauth/token"), {
            method: "POST",
            body: new URLSearchParams(payload),
            headers: {
              "content-type": "application/x-www-form-urlencoded",
            },
          });
        } catch (e) {
          return new AuthServerUnreachable({ cause: e });
        }

        if (isTransient(tokenResponse.status)) {
          return new AuthServerUnreachable();
        }

        if (tokenResponse.status === 401) {
          get().logout();
          return new InvalidCredentials();
        }

        let responseJSON;
        try {
          responseJSON = await tokenResponse.json();
        } catch (e) {
          get().logout();
          return new UnknownLoginError(null, { cause: e });
        }

        if (tokenResponse.status >= 300) {
          get().logout();
          return new UnknownLoginError(responseJSON);
        }

        const accessTokenPayload: TokenPayload = JSON.parse(
          atob(responseJSON.access_token.split(".")[1]),
        );
        const refreshTokenPayload = JSON.parse(
          atob(responseJSON.refresh_token.split(".")[1]),
        );

        set((state) => ({
          accessToken: responseJSON.access_token,
          refreshToken: responseJSON.refresh_token,
          accessTokenPayload: accessTokenPayload,
          username: accessTokenPayload.email || state.username,
          accessTokenExpiry: DateTime.fromSeconds(accessTokenPayload.exp),
          refreshTokenExpiry: DateTime.fromSeconds(refreshTokenPayload.exp),
        }));
        return null;
      }

      let refreshInFlight: Promise<Error | null> | null = null;

      async function attemptRefresh(
        refreshToken: string,
      ): Promise<Error | null> {
        const attempt = () =>
          authenticate({
            grant_type: "refresh_token",
            refresh_token: refreshToken,
          });

        let error = await attempt();

        for (const delayMs of REFRESH_RETRY_DELAYS_MS) {
          if (!(error instanceof AuthServerUnreachable)) break;
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          error = await attempt();
        }

        return error;
      }

      function refreshTokens(refreshToken: string): Promise<Error | null> {
        refreshInFlight ??= attemptRefresh(refreshToken).finally(() => {
          refreshInFlight = null;
        });
        return refreshInFlight;
      }

      return {
        accessToken: null,
        refreshToken: null,
        username: null,
        accessTokenExpiry: null,
        accessTokenPayload: null,
        refreshTokenExpiry: null,
        agroManagerEnv: null,

        async login({ email, password }) {
          const error = await authenticate({
            username: email,
            password,
            grant_type: "password",
          });
          if (error !== null) {
            return error;
          }

          set(() => ({
            username: email,
          }));
          return null;
        },

        async setTokens(tokens: Tokens, email: string) {
          const accessTokenPayload: TokenPayload = JSON.parse(
            atob(tokens.access_token.split(".")[1]),
          );
          const refreshTokenPayload = JSON.parse(
            atob(tokens.refresh_token.split(".")[1]),
          );

          set(() => ({
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            accessTokenPayload: accessTokenPayload,
            accessTokenExpiry: DateTime.fromSeconds(accessTokenPayload.exp),
            refreshTokenExpiry: DateTime.fromSeconds(refreshTokenPayload.exp),
            username:
              email || accessTokenPayload.email || accessTokenPayload.id,
          }));
        },

        setAgroManagerEnv(env: string | null) {
          set({ agroManagerEnv: env });
        },

        async getToken(): Promise<string | null> {
          const {
            accessToken,
            accessTokenExpiry,
            refreshToken,
            refreshTokenExpiry,
          } = get();

          if (accessToken === null) {
            get().logout();
            return null;
          }

          if (
            accessToken &&
            accessTokenExpiry &&
            accessTokenExpiry > DateTime.now()
          ) {
            sendTokenToServiceWorkers(accessToken);
            return accessToken;
          }

          if (
            !refreshToken ||
            (refreshTokenExpiry && refreshTokenExpiry < DateTime.now())
          ) {
            get().logout();
            return null;
          }

          const error = await refreshTokens(refreshToken);
          if (error instanceof AuthServerUnreachable) {
            throw error;
          }

          const acc_token = get().accessToken;
          sendTokenToServiceWorkers(acc_token);
          return acc_token;
        },

        logout() {
          set(() => ({
            accessTokenExpiry: null,
            refreshTokenExpiry: null,
            accessToken: null,
            accessTokenPayload: null,
            refreshToken: null,
            username: null,
            agroManagerEnv: null,
          }));
        },
      };
    },
    {
      name: "aureaAuth",
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const { state } = JSON.parse(str);
          return {
            state: {
              ...state,
              accessTokenExpiry:
                typeof state.accessTokenExpiry === "string"
                  ? DateTime.fromISO(state.accessTokenExpiry)
                  : state.accessTokenExpiry,
              refreshTokenExpiry:
                typeof state.refreshTokenExpiry === "string"
                  ? DateTime.fromISO(state.refreshTokenExpiry)
                  : state.refreshTokenExpiry,
            },
          };
        },
        setItem: (name, newValue) =>
          localStorage.setItem(name, JSON.stringify(newValue)),
        removeItem: (name) => localStorage.removeItem(name),
      },
    },
  ),
);

function sendTokenToServiceWorkers(accessToken: string | null) {
  navigator.serviceWorker?.controller?.postMessage?.({
    type: "GET_TOKEN",
    token: accessToken,
  });
}
